import type { Rule } from "antd/lib/form";
import i18n from "i18next";

import type { FormConfigurationEntity } from "../../../../../components/Form/types/FormConfigurationEntity";

/**
 * Patches the backend form definition to add a conditional required validation
 * rule to the `hostTerminalIdMask` field.
 *
 * The field is required only when `hostTerminalIdType === "GENERATE"`.
 * Any static "Required" rule from the backend config is replaced with this
 * dynamic one so validation fires correctly when switching between types.
 */
export const mergeHostTerminalIdMaskFields = (
    formConfig: FormConfigurationEntity["formConfiguration"],
): FormConfigurationEntity["formConfiguration"] => {
    if (!formConfig) return formConfig;

    const conditionalRequiredRule: Rule = ({ getFieldValue }) => ({
        validator(_: unknown, value: string) {
            if (getFieldValue("hostTerminalIdType") === "GENERATE" && !value) {
                return Promise.reject(i18n.t("error.acquirer.hostTerminalIdMask.required"));
            }
            return Promise.resolve();
        },
    });

    return {
        ...formConfig,
        tabs: formConfig.tabs.map((tab) => {
            const maskFieldIndex = tab.fields.findIndex((f) => f.name === "hostTerminalIdMask");
            if (maskFieldIndex === -1) return tab;

            const maskField = tab.fields[maskFieldIndex];

            // Strip any static Required rule from the backend config — we handle it dynamically.
            const validationRulesWithoutRequired = maskField.attributes?.validationRules?.filter((r) => r.name !== "Required") ?? [];

            const patchedField = {
                ...maskField,
                attributes: {
                    ...maskField.attributes,
                    validationRules: validationRulesWithoutRequired,
                },
                rules: [conditionalRequiredRule],
            };

            const newFields = [...tab.fields];
            newFields[maskFieldIndex] = patchedField;

            return { ...tab, fields: newFields };
        }),
    };
};
